/**
 * The search service's appointment list view (`POST
 * /search/appointments-list-view`): one page of a Portal list page, served
 * from the `appointment_details` index with the page scope, filters, sort and
 * paging owned by the server. The counterpart of `listAppointmentsOptimized`
 * for `useAppointmentsList` when the list-view flag is on.
 *
 * Items come back as the index's flat projection and are widened here into the
 * `AppointmentRecord` shape every dash cell already reads, so pages do not
 * branch on the read path. The record is partial: only the view's projected
 * fields are set (`views.list_fields` on the server), the rest are undefined.
 */
import type {
  AppointmentRecord,
  AppointmentStatus,
  AppointmentType,
  BillingStatus,
  BodyPart,
  InquiringParty,
  Language,
  NoShowReason,
  OpsChecklist,
  ParticipantType,
  Region,
  ReportSource,
  ReportStatus,
  SpecialtyType,
} from "~/api/API";
import { createSearchServiceClient } from "~/api/apiClient";
import type { components } from "~/api/generated/search-service-types";

export type AppointmentListViewRequest =
  components["schemas"]["AppointmentListRequest"];
export type AppointmentListViewItem =
  components["schemas"]["AppointmentListItem"];

/** Checklist key → the cell's status, per appointment id (spec D5: the
 *  value the row was filtered on). A key the hit cannot answer is absent. */
export type ListViewChecklistStatuses = Record<string, Record<string, string>>;

export interface AppointmentListViewPage {
  appointments: AppointmentRecord[];
  /** The `search_after` cursor for the next page; `null` on the last one. */
  nextToken: string | null;
  /** Every match in scope, not only the loaded pages. */
  total: number;
  checklist: ListViewChecklistStatuses;
  /** Case readiness by appointment id (spec D9), for rows of the views that
   *  read it; absent elsewhere. */
  caseReady: Record<string, boolean>;
}

/**
 * The list view's keys for the checklist columns the dash computed in the
 * browser; the stored columns keep their `OpsTodoKey`. The physician keys are
 * the doctor's HOI / Records columns.
 */
export type ListViewChecklistKey =
  | "interpreter"
  | "assessment"
  | "records"
  | "zoom_link"
  | "notice"
  | "payment_received"
  | "physician_hoi"
  | "physician_records";

/**
 * The cursor no longer matches its query (the server's fingerprint covers the
 * request, role, scope and today), so paging must restart from page 1.
 */
export class AppointmentListViewCursorError extends Error {
  constructor() {
    super("Appointment list cursor rejected; restarting from the first page");
    this.name = "AppointmentListViewCursorError";
  }
}

/** OpenSearch answered from part of the index (502); the same request may succeed. */
export class AppointmentListViewRetryableError extends Error {
  constructor(status: number) {
    super(`Appointment list search incomplete (${status})`);
    this.name = "AppointmentListViewRetryableError";
  }
}

const relation = (
  id: string | null | undefined,
  name: string | null | undefined,
) => (id || name ? { id: id ?? "", name: name ?? null } : null);

/**
 * Widen one index item into the dash's `AppointmentRecord` shape.
 *
 * The one cast in this module: the item is a network payload with the view's
 * projected fields, and every nested type (`Doctor`, `Location`, `PatientCase`)
 * has required columns the index does not carry. `doctor_name` is the full
 * display name, so it goes in `firstName`, which is what
 * `formatDoctorDisplayName` joins. The billing fields (billing views only) are
 * the server's DynamoDB read, set back where the billing cells read them.
 */
export const toAppointmentRecord = (
  item: AppointmentListViewItem,
): AppointmentRecord => {
  const opsRole = {
    schedulingAssistant: relation(
      item.scheduling_assistant_id,
      item.scheduling_assistant_name,
    ),
    medicalAssistant: relation(
      item.medical_assistant_id,
      item.medical_assistant_name,
    ),
    reportOp: relation(item.report_op_id, item.report_op_name),
    billingOp: relation(item.billing_op_id, item.billing_op_name),
    onSiteOp: relation(item.on_site_op_id, item.on_site_op_name),
    preVisitReviewer: relation(
      item.pre_visit_reviewer_id,
      item.pre_visit_reviewer_name,
    ),
  };
  // The examined parts span every linked case: the primary case's first, then
  // the other cases' parts it does not already list.
  const bodyParts = [
    ...new Set([
      ...(item.injured_body_parts ?? []),
      ...(item.extra_injured_body_parts ?? []),
    ]),
  ];
  const injuredBodyParts = bodyParts.length ? (bodyParts as BodyPart[]) : null;
  const patient =
    item.patient_id || item.patient_name || item.patient_language
      ? {
          id: item.patient_id ?? "",
          name: item.patient_name ?? null,
          language: (item.patient_language as Language | undefined) ?? null,
        }
      : null;
  const billingInfo = {
    totalCharge: item.total_charge ?? null,
    amountPaid: item.amount_paid ?? null,
    billedDate: item.billed_date ?? null,
    paidDate: item.paid_date ?? null,
  };
  const record = {
    __typename: "AppointmentRecord",
    id: item.id,
    date: item.date,
    time: item.time,
    durationMinutes: item.duration_minutes,
    type: item.appointment_type as AppointmentType | null | undefined,
    status: item.status as AppointmentStatus | null | undefined,
    reportStatus: item.report_status as ReportStatus | null | undefined,
    billingStatus: item.billing_status as BillingStatus | null | undefined,
    inquiryDate: item.inquiry_date,
    reportDue: item.report_due,
    reportSubmitted: item.report_submitted,
    difficultyLevel: item.difficulty_level,
    panelSpecialty: item.panel_specialty as SpecialtyType | null | undefined,
    noShowReason: item.no_show_reason as NoShowReason | null | undefined,
    reschedulingRequestedBy: item.rescheduling_requested_by as
      | ParticipantType
      | null
      | undefined,
    reschedulingRequestedInternally: item.rescheduling_requested_internally,
    doctorID: item.doctor_id,
    doctor: item.doctor_id
      ? {
          id: item.doctor_id,
          firstName: item.doctor_name ?? null,
          collectionRate: item.doctor_collection_rate ?? null,
        }
      : null,
    locationID: item.location_id,
    location: item.location_id
      ? {
          id: item.location_id,
          name: item.location_name ?? null,
          region: (item.location_region as Region | undefined) ?? null,
        }
      : null,
    patientCases: patient
      ? {
          items: [
            {
              patientCase: {
                id: "",
                patientID: patient.id,
                patient,
                claimNumber: item.primary_claim_number ?? null,
                injuryInfo: { injuredBodyParts },
              },
            },
          ],
        }
      : null,
    inquirerInfo: item.requesting_party
      ? { inquiringParty: item.requesting_party as InquiringParty }
      : null,
    appointmentRecordSchedulingAssistantId: item.scheduling_assistant_id,
    appointmentRecordMedicalAssistantId: item.medical_assistant_id,
    appointmentRecordReportOpId: item.report_op_id,
    appointmentRecordBillingOpId: item.billing_op_id,
    appointmentRecordOnSiteOpId: item.on_site_op_id,
    appointmentRecordPreVisitReviewerId: item.pre_visit_reviewer_id,
    ...opsRole,
    historyCallDate: item.history_call_date,
    historyCallTime: item.history_call_time,
    interpreter:
      item.interpreter_name || item.interpreter_certificate_id
        ? {
            name: item.interpreter_name ?? null,
            certificateId: item.interpreter_certificate_id ?? null,
            confirmed: item.interpreter_confirmed ?? null,
          }
        : null,
    assessment:
      item.assessment_marked_as_done === undefined ||
      item.assessment_marked_as_done === null
        ? null
        : { markedAsDone: item.assessment_marked_as_done },
    billingInfo: Object.values(billingInfo).some((value) => value !== null)
      ? billingInfo
      : null,
    reportConfiguration: item.report_assessment_source
      ? { assessment: item.report_assessment_source as ReportSource }
      : null,
    // Same keys as the stored map, so `storedChecklist.ts` reads it as is.
    opsChecklist:
      (item.ops_checklist as OpsChecklist | null | undefined) ?? null,
  };
  return record as unknown as AppointmentRecord;
};

export const fetchAppointmentListView = async (
  request: AppointmentListViewRequest,
): Promise<AppointmentListViewPage> => {
  const client = createSearchServiceClient();
  // Non-2xx statuses resolve with `error` and are reported by the client's
  // middleware; only the two the hook acts on get a typed error here.
  const { data, error, response } = await client.POST(
    "/search/appointments-list-view",
    { body: request },
  );
  if (error || !data) {
    if (response.status === 400 && request.cursor) {
      throw new AppointmentListViewCursorError();
    }
    if (response.status === 502) {
      throw new AppointmentListViewRetryableError(response.status);
    }
    throw new Error(
      `Failed to list appointments (${response.status}): ${JSON.stringify(error)}`,
    );
  }
  const checklist: ListViewChecklistStatuses = {};
  const caseReady: Record<string, boolean> = {};
  for (const item of data.items) {
    if (item.checklist) checklist[item.id] = item.checklist;
    if (item.case_ready != null) caseReady[item.id] = item.case_ready;
  }
  return {
    appointments: data.items.map(toAppointmentRecord),
    nextToken: data.next_cursor ?? null,
    total: data.total,
    checklist,
    caseReady,
  };
};
